import { storeToRefs } from "pinia";
import { computed } from "vue";

import { convertSocialTypesToMentionTypes } from "shared/helpers/media";
import { randomUUID } from "shared/helpers/uuid";
import type {
  ChartSetting,
  SavedChartSetting,
} from "shared/modules/Charts/types";
import { TopArticlesMetric } from "shared/modules/Charts/types/chartSettings";
import {
  isChartSettingWithLinkedSocialStreams,
  isChartSettingWithMediaReleases,
  isChartSettingWithMentionStreamsAndSocialStreams,
  isChartSettingWithPostSelection,
  isChartSettingWithSocialStreamsOnly,
  isChartSettingWithSortBy,
  isChartSettingWithSovMetricType,
  isChartSettingWithStreamsAndMediaTypes,
} from "shared/modules/Charts/types/guards";
import { useInsightsChart } from "shared/modules/InsightsChart/composables/useInsightsChart";
import { useInsightsDashboardStore } from "shared/modules/InsightsDashboard/stores/useInsightsDashboardStore";
import type {
  InsightsDashboardTemplate,
  InsightsDashboardTemplateChart,
  InsightsDashboardTemplateChartDefaults,
  InsightsDashboardTemplateChartKey,
  InsightsDashboardTemplateConfig,
} from "shared/modules/InsightsDashboard/types/dashboardTemplates";
import {
  getChartKey,
  INSIGHTS_DASHBOARD_TEMPLATES,
  isChartWithDefaults,
} from "shared/modules/InsightsDashboard/types/dashboardTemplates";
import { useInstantInsightsCharts } from "shared/modules/InstantInsights/composables/useInstantInsightsCharts";
import features from "shared/services/features";

export function useInsightsDashboardTemplates() {
  const { chartsIndex } = useInstantInsightsCharts();

  const insightsDashboardStore = useInsightsDashboardStore();

  const { availableCharts, socialAccountOptions } = storeToRefs(
    insightsDashboardStore
  );

  const { getDefaultChartSetting, getDefaultStream } = insightsDashboardStore;

  const availableTemplates = computed<InsightsDashboardTemplate[]>(() =>
    INSIGHTS_DASHBOARD_TEMPLATES.filter((template) =>
      features.has(template.featureFlag)
    ).map((template) => ({
      ...template,
      charts: template.charts.filter(
        (chart) =>
          Boolean(chartsIndex.value[getChartKey(chart)]) &&
          isChartAllowedByFeatures(chart)
      ),
    }))
  );

  function isChartAllowedByFeatures(
    chart: InsightsDashboardTemplateChart
  ): boolean {
    return (
      !isChartWithDefaults(chart) ||
      chart.defaults.sort_by !== TopArticlesMetric.AdvertisementRate ||
      features.has("advertising_value_mentions")
    );
  }

  function getDefaultStreamIdsForChart(
    chartKey: InsightsDashboardTemplateChartKey,
    config: InsightsDashboardTemplateConfig
  ): number[] {
    const baseSetting: ChartSetting = { chart: chartKey };

    let streamIds: number[] = [];

    if (isChartSettingWithSocialStreamsOnly(baseSetting)) {
      streamIds = config.socialStreamIds ?? [];
    } else if (isChartSettingWithMentionStreamsAndSocialStreams(baseSetting)) {
      streamIds = [
        ...(config.mentionStreamIds ?? []),
        ...(config.socialStreamIds ?? []),
      ];
    } else if (isChartSettingWithStreamsAndMediaTypes(baseSetting)) {
      streamIds = config.mentionStreamIds ?? [];
    }

    if (!streamIds.length) {
      const defaultStream = getDefaultStream({
        chart: chartKey,
      } as SavedChartSetting);

      return defaultStream ? [defaultStream.id] : [];
    }

    return streamIds;
  }

  function applyTemplateConfigToChart(
    chartSetting: SavedChartSetting,
    config: InsightsDashboardTemplateConfig
  ): SavedChartSetting {
    const result: SavedChartSetting = { ...chartSetting };

    if (isChartSettingWithMediaReleases(result)) {
      result.media_release_ids = config.mediaOutreachIds ?? [];
    }

    if (isChartSettingWithSocialStreamsOnly(result)) {
      if (config.socialStreamIds?.length) {
        result.stream_ids = config.socialStreamIds;
      }

      if (config.socialMentionTypes?.length) {
        result.types = convertSocialTypesToMentionTypes(
          config.socialMentionTypes
        );
      }
    } else if (isChartSettingWithMentionStreamsAndSocialStreams(result)) {
      const mentionIds = config.mentionStreamIds ?? [];
      const socialIds = config.socialStreamIds ?? [];

      if (mentionIds.length || socialIds.length) {
        result.stream_ids = [...mentionIds, ...socialIds];
      }

      const mentionTypes = config.mentionTypes?.length
        ? config.mentionTypes
        : undefined;

      const socialTypes = config.socialMentionTypes?.length
        ? convertSocialTypesToMentionTypes(config.socialMentionTypes)
        : undefined;

      if (mentionTypes || socialTypes) {
        result.types = [...(mentionTypes ?? []), ...(socialTypes ?? [])];
      }
    } else if (isChartSettingWithStreamsAndMediaTypes(result)) {
      if (config.mentionStreamIds?.length) {
        result.stream_ids = config.mentionStreamIds;
      }

      if (typeof config.mentionTypes !== "undefined") {
        result.types = config.mentionTypes;
      }
    }

    if (isChartSettingWithLinkedSocialStreams(result)) {
      const mentionStreamId = config.mentionStreamIds?.[0];
      const socialStreamId = config.socialStreamIds?.[0];

      if (mentionStreamId && socialStreamId) {
        result.linked_stream_ids = { [mentionStreamId]: socialStreamId };
      }
    }

    if (isChartSettingWithPostSelection(result) && config.socialPostId) {
      result.post_ids = [config.socialPostId];
    }

    return result;
  }

  function applyChartDefaults(
    chartSetting: SavedChartSetting,
    defaults: InsightsDashboardTemplateChartDefaults
  ): SavedChartSetting {
    const result = { ...chartSetting };

    if (defaults.sort_by !== undefined && isChartSettingWithSortBy(result)) {
      result.sort_by = defaults.sort_by;
    }

    if (defaults.chart_variant !== undefined) {
      result.chart_variant = defaults.chart_variant;
    }

    if (
      defaults.sov_metric_type !== undefined &&
      isChartSettingWithSovMetricType(result)
    ) {
      result.metric_type = defaults.sov_metric_type;
    }

    return result;
  }

  function createChartSettingFromTemplate(
    chartEntry: InsightsDashboardTemplateChart,
    config: InsightsDashboardTemplateConfig
  ): SavedChartSetting {
    const chartKey = getChartKey(chartEntry);

    const chartDefaults = isChartWithDefaults(chartEntry)
      ? chartEntry.defaults
      : undefined;

    const defaultStreamIds = getDefaultStreamIdsForChart(chartKey, config);

    const { getInitialChartSetting } = useInsightsChart({
      chartSetting: null,
      defaultChartSettings: availableCharts.value!,
      defaultRange: config.dateRange,
      defaultStreamIds,
      socialAccountOptions: socialAccountOptions.value,
    });

    const defaultSetting = getDefaultChartSetting(chartKey);

    const baseChartSetting: SavedChartSetting = {
      advanced_filters: null,
      chart: chartKey,
      chart_variant: defaultSetting?.default_chart_variant,
      info_tooltip: "",
      label: "",
      range: config.dateRange,
      uuid: randomUUID(),
    };

    const initializedChart: SavedChartSetting = {
      ...baseChartSetting,
      ...getInitialChartSetting(baseChartSetting),
    };

    const chartWithConfig = applyTemplateConfigToChart(
      initializedChart,
      config
    );

    if (chartDefaults) {
      return applyChartDefaults(chartWithConfig, chartDefaults);
    }

    return chartWithConfig;
  }

  return {
    availableTemplates,
    createChartSettingFromTemplate,
  };
}
